import React, { useCallback, useState, useEffect, useContext, useMemo } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  Form,
  Col,
  Row,
  Card,
  Badge,
  Spinner,
  InputGroup,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "@themesberg/react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import Cards from "react-credit-cards";
import {
  createTokenPin,
  fetchApi,
  getBalanceUrl,
  postApi,
  verifyToken,
} from "../../../redux/api";
import CustomTable from "../../../components/CustomTable";
import { Routes } from "../../../routes";
import { SearchContext } from "../../HomePage";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchInput from "../../../components/SearchInput";
import DaterangeSelector from "../../../components/DaterangeSelector";
import {
  faSearch,
  faFilter,
  faSyncAlt,
  faEye,
  faCalendarAlt,
  faFileExport,
  faCog,
  faChartBar,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "react-bootstrap";
// bankList now fetched from API via useBankList hook
import useBankList from "../../../hooks/useBankList";
import toast from "react-hot-toast";
import CustomButton from "../../../components/CustomButton";
import moment from "moment";

function STReceivingOffice({
  status = "pending",
  pageType = "batch",
  type = "main_treasury",
}) {
  const [pin, setPin] = useState("");
  const [item, setItem] = useState({});
  const [modalIsOpen1, setModalIsOpen1] = useState(false);
  const toggle1 = () => setModalIsOpen1((p) => !p);
  const [selected, setSelected] = useState({});
  const [focus, setFocus] = useState("name");
  const user = useSelector((state) => state.auth.user);
  const { searchTerm, setSearchTerm } = useContext(SearchContext);
  const location = useLocation();
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const history = useNavigate();
  const { bankList } = useBankList();
  const isST = location.pathname.includes("batch-process-st");

  // Enhanced state management
  const [paymentSchedule, setPaymentScheduleTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Advanced filtering state
  const [filters, setFilters] = useState({
    searchTerm: "",
    dateRange: {
      startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD')
    },
    amountRange: {
      min: "",
      max: ""
    },
    voucherCountRange: {
      min: "",
      max: ""
    },
    status: "all"
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // UI state
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // table, card

  // Office type detection
  const isReceivingOffice = location.pathname.includes("receiving-office");
  const isCheckingOffice = location.pathname.includes("checking-office");
  const isDSTOffice = location.pathname.includes("dst-office");
  const isHeadChequesOffice = location.pathname.includes("head-cheques-office");
  const isDSTPendingSchedule = location.pathname.includes("dst-pending-schedule");
  const isSTReview = location.pathname.includes("sub-treasurer-payment-review");
  const isApprovedPayment = location.pathname.includes("approved-payments");
  const isRecurrentCashOffice = location.pathname.includes("recurrent-cash-office");
  const isAlliedCashOffice = location.pathname.includes("allied-cash-office");
  const isExpenditureCashOffice = location.pathname.includes("expenditure-cash-office");
  const isUpdatePage = pageType === "update";
  const isCommitPage = pageType === "commit";
  const isCommittedPage = pageType === "committed";
  const isRemittancePage = pageType === "remittance";

  // Get office title and status
  const getOfficeInfo = () => {
    if (isReceivingOffice) return { title: "Receiving Office", status: "Director Expenditure Control Approved" };
    if (isCheckingOffice) return { title: "Checking Office", status: "Schedule Approved By Receiving Office" };
    if (isDSTOffice) return { title: "DST Office", status: "Schedule Approved By Checking Office" };
    if (isHeadChequesOffice) return { title: "Head Cheques Office", status: "Schedule Approved By DST" };
    if (isDSTPendingSchedule) return { title: "DST Pending Schedule", status: "Schedule Approved By Cheques Office" };
    if (isSTReview) return { title: "Sub Treasurer Review", status: "Schedule Approved By DST" };
    if (isApprovedPayment) return { title: "Approved Payments", status: "Payment Approved By Sub Treasurer" };
    if (isRecurrentCashOffice) return { title: "Cash Office (RECURRENT)", status: "Schedule Approved By DST" };
    if (isAlliedCashOffice) return { title: "Cash Office (ALLIED)", status: "Schedule Approved By DST" };
    if (isExpenditureCashOffice) return { title: "Cash Office (EXPENDITURE)", status: "Schedule Approved By DST" };
    return { title: "Office", status: "" };
  };

  const officeInfo = getOfficeInfo();

  // Enhanced table fields with better styling
  const fields = [
    {
      title: "S/N",
      custom: true,
      component: (item, idx) => (
        <Badge
          bg="light"
          text="dark"
          className="fs-6 fw-bold"
        >
          {(currentPage - 1) * itemsPerPage + idx + 1}
        </Badge>
      ),
      style: { width: '60px', textAlign: 'center' }
    },
    {
      title: "Batch Information",
      custom: true,
      component: (item) => (
        <div>
          <div className="fw-bold text-primary mb-1">{item.batch_number}</div>
          <small className="text-muted">
            Created: {moment(item.created_at).format('MMM DD, YYYY')}
          </small>
          {item.status === "returned" && (
            <Badge bg="warning" className="ms-2">Returned</Badge>
          )}
        </div>
      ),
      style: { minWidth: '200px' }
    },
    {
      title: "Vouchers",
      custom: true,
      component: (item) => (
        <div className="text-center">
          <div className="fw-bold fs-5 text-info">{item.no_of_payments}</div>
          <small className="text-muted">vouchers</small>
        </div>
      ),
      style: { width: '100px', textAlign: 'center' }
    },
    {
      title: "Total Amount",
      custom: true,
      component: (item) => (
        <div className="text-end">
          <div className="fw-bold fs-6 text-success">
            ₦{parseFloat(item.total_amount).toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </div>
          <small className="text-muted">
            Avg: ₦{(parseFloat(item.total_amount) / parseInt(item.no_of_payments)).toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            })}
          </small>
        </div>
      ),
      style: { minWidth: '150px' }
    },
    {
      title: "Status",
      custom: true,
      component: (item) => {
        const getStatusBadge = () => {
          if (item.status === "returned") return <Badge bg="warning">Returned</Badge>;
          if (item.status === "processed") return <Badge bg="success">Processed</Badge>;
          return <Badge bg="info">Pending</Badge>;
        };
        return (
          <div className="text-center">
            {getStatusBadge()}
          </div>
        );
      },
      style: { width: '100px', textAlign: 'center' }
    },
    {
      title: "Actions",
      custom: true,
      component: (item) => (
        <div className="d-flex gap-2 justify-content-center">
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Process this batch</Tooltip>}
          >
            <Button
              size="sm"
              variant="success"
              onClick={() => handleProcessClick(item)}
              className="d-flex align-items-center gap-1"
            >
              <FontAwesomeIcon icon={faEye} />
              Process
            </Button>
          </OverlayTrigger>
        </div>
      ),
      style: { width: '120px', textAlign: 'center' }
    },
  ];

  // Handle process click
  const handleProcessClick = (item) => {
    if (isReceivingOffice) {
      history(`${Routes.subTreasury.receivingOfficeViewSchedule}/${item.batch_number}`);
    } else if (isCheckingOffice) {
      history(`${Routes.subTreasury.checkingOfficeViewSchedule}/${item.batch_number}`);
    } else if (isHeadChequesOffice) {
      history(`${Routes.subTreasury.headChequesOfficeViewSchedule}/${item.batch_number}`);
    } else if (isDSTPendingSchedule) {
      history(`${Routes.subTreasury.dstPendingScheduleOfficeViewSchedule}/${item.batch_number}`);
    } else if (isSTReview) {
      history(`${Routes.subTreasury.subTreasurerPaymentReviewOfficeViewSchedule}/${item.batch_number}`);
    } else if (isApprovedPayment) {
      history(`${Routes.subTreasury.approvedPaymentsViewSchedule}/${item.batch_number}`);
    } else if (isDSTOffice) {
      history(`${Routes.subTreasury.dstOfficeViewSchedule}/${item.batch_number}`);
    } else if (isRecurrentCashOffice) {
      history(`${Routes.subTreasury.recurrentCashOfficeViewScheduleDynamic.replace(':batch_no', item.batch_number)}`);
    } else if (isAlliedCashOffice) {
      history(`${Routes.subTreasury.alliedCashOfficeViewScheduleDynamic.replace(':batch_no', item.batch_number)}`);
    } else if (isExpenditureCashOffice) {
      history(`${Routes.subTreasury.expenditureCashOfficeViewScheduleDynamic.replace(':batch_no', item.batch_number)}`);
    }
  };

  // Account info state
  const [accountInfo, setAccountInfo] = useState([]);

  const getCard = useCallback(() => {
    setLoading(true);
    fetchApi(`get-details/by-type?type=SUB-TREASURY ACCOUNT`)
      .then(async (resp) => {
        setLoading(false);
        if (resp && resp.success) {
          let arr = [];
          resp.data.forEach((item) => {
            let selected = bankList.find((i) => i.code === item.bank_code);
            arr.push({ ...item, bankName: selected?.title || 'Unknown Bank' });
          });
          setAccountInfo(arr);
        }
      })
      .catch((err) => {
        setLoading(false);
        setError("Failed to load account information");
        console.log(err);
      });
  }, [bankList]);

  useEffect(() => {
    getCard();
  }, [getCard]);

  const getBalance = useCallback(
    async (id) => {
      setBalanceLoading(true);
      try {
        const resp = await fetchApi(await getBalanceUrl(id, pin));
        setBalanceLoading(false);
        if (resp && resp.success) {
          setSelected(resp);
          setPin("");
          setModalIsOpen1(false);
          setModalIsOpen(true);
        } else {
          toast.error(resp?.message ?? "Failed to get balance");
        }
      } catch (err) {
        setBalanceLoading(false);
        toast.error("Error while getting the balance. Please try again.");
        console.log(err);
      }
    },
    [pin]
  );

  // Enhanced data fetching with error handling
  const fetchPaymentSchedule = useCallback(async () => {
    setLoading(true);
    setError(null);

    const isCashOffice = isRecurrentCashOffice || isAlliedCashOffice || isExpenditureCashOffice;

    try {
      const data = await postApi(`get_batch_list`, {
        query_type: isCashOffice ? "select_by_pay_kind" : "select",
        status: officeInfo.status,
        start_date: filters.dateRange.startDate,
        end_date: filters.dateRange.endDate
      });

      if (data.result) {
        let results = data.result;
        if (isCashOffice) {
          if (isRecurrentCashOffice) {
            results = results.filter(item => item.payment_kind === "recurrent");
          } else if (isAlliedCashOffice) {
            results = results.filter(item => item.payment_kind === "allied");
          } else if (isExpenditureCashOffice) {
            results = results.filter(item => item.payment_kind === "expenditure");
          }
        }
        setPaymentScheduleTable(results);
      } else {
        setPaymentScheduleTable([]);
      }
    } catch (err) {
      setError("Failed to load payment schedules. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [officeInfo.status, filters.dateRange, isRecurrentCashOffice, isAlliedCashOffice, isExpenditureCashOffice]);

  useEffect(() => {
    fetchPaymentSchedule();
  }, [fetchPaymentSchedule]);

  // Enhanced filtering logic
  const filteredData = useMemo(() => {
    let filtered = paymentSchedule;

    // Search filter
    if (filters.searchTerm) {
      filtered = filtered.filter(item =>
        item.batch_number?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        item.no_of_payments?.toString().includes(filters.searchTerm) ||
        item.total_amount?.toString().includes(filters.searchTerm)
      );
    }

    // Amount range filter
    if (filters.amountRange.min || filters.amountRange.max) {
      filtered = filtered.filter(item => {
        const amount = parseFloat(item.total_amount);
        const min = filters.amountRange.min ? parseFloat(filters.amountRange.min) : 0;
        const max = filters.amountRange.max ? parseFloat(filters.amountRange.max) : Infinity;
        return amount >= min && amount <= max;
      });
    }

    // Voucher count filter
    if (filters.voucherCountRange.min || filters.voucherCountRange.max) {
      filtered = filtered.filter(item => {
        const count = parseInt(item.no_of_payments);
        const min = filters.voucherCountRange.min ? parseInt(filters.voucherCountRange.min) : 0;
        const max = filters.voucherCountRange.max ? parseInt(filters.voucherCountRange.max) : Infinity;
        return count >= min && count <= max;
      });
    }

    // Status filter
    if (filters.status !== "all") {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    return filtered;
  }, [paymentSchedule, filters]);

  // Pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPaymentSchedule();
    setRefreshing(false);
    toast.success("Data refreshed successfully");
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Handle date range change
  const handleDateRangeChange = (startDate, endDate) => {
    setFilters(prev => ({
      ...prev,
      dateRange: { startDate, endDate }
    }));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      searchTerm: "",
      dateRange: {
        startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD')
      },
      amountRange: { min: "", max: "" },
      voucherCountRange: { min: "", max: "" },
      status: "all"
    });
    setCurrentPage(1);
  };

  // Statistics
  const stats = useMemo(() => {
    const totalAmount = filteredData.reduce((sum, item) => sum + parseFloat(item.total_amount || 0), 0);
    const totalVouchers = filteredData.reduce((sum, item) => sum + parseInt(item.no_of_payments || 0), 0);

    return {
      totalBatches: filteredData.length,
      totalAmount,
      totalVouchers,
      averageAmount: filteredData.length > 0 ? totalAmount / filteredData.length : 0
    };
  }, [filteredData]);

  return (
    <>
      {/* Header Section */}
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-flex flex-column">
          <div className="d-flex align-items-center gap-3 mb-2">
            <h4 className="mb-0">{officeInfo.title}</h4>
            <Badge bg="info" className="fs-6">
              {filteredData.length} batches
            </Badge>
          </div>
          <p className="text-muted mb-0">
            Manage and process payment batches for {officeInfo.title.toLowerCase()}
          </p>
        </div>

        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="d-flex align-items-center gap-2"
          >
            <FontAwesomeIcon
              icon={faSyncAlt}
              className={refreshing ? "fa-spin" : ""}
            />
            Refresh
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="d-flex align-items-center gap-2"
          >
            <FontAwesomeIcon icon={faFilter} />
            Filters
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="text-primary mb-2">
                <FontAwesomeIcon icon={faChartBar} size="2x" />
              </div>
              <h5 className="mb-1">{stats.totalBatches}</h5>
              <small className="text-muted">Total Batches</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="text-success mb-2">
                <FontAwesomeIcon icon={faFileExport} size="2x" />
              </div>
              <h5 className="mb-1">{stats.totalVouchers}</h5>
              <small className="text-muted">Total Vouchers</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="text-warning mb-2">
                <FontAwesomeIcon icon={faInfoCircle} size="2x" />
              </div>
              <h5 className="mb-1">
                ₦{stats.totalAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </h5>
              <small className="text-muted">Total Amount</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="text-info mb-2">
                <FontAwesomeIcon icon={faCog} size="2x" />
              </div>
              <h5 className="mb-1">
                ₦{stats.averageAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                })}
              </h5>
              <small className="text-muted">Average Amount</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters Section */}
      {showFilters && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Advanced Filters</h6>
              <Button variant="link" size="sm" onClick={clearFilters}>
                Clear All
              </Button>
            </div>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Search</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by batch number, amount, or voucher count..."
                      value={filters.searchTerm}
                      onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Date Range</Form.Label>
                  <DaterangeSelector
                    startDate={filters.dateRange.startDate}
                    endDate={filters.dateRange.endDate}
                    onChange={handleDateRangeChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount Range</Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="number"
                        placeholder="Min amount"
                        value={filters.amountRange.min}
                        onChange={(e) => handleFilterChange('amountRange', {
                          ...filters.amountRange,
                          min: e.target.value
                        })}
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        type="number"
                        placeholder="Max amount"
                        value={filters.amountRange.max}
                        onChange={(e) => handleFilterChange('amountRange', {
                          ...filters.amountRange,
                          max: e.target.value
                        })}
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Voucher Count Range</Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="number"
                        placeholder="Min count"
                        value={filters.voucherCountRange.min}
                        onChange={(e) => handleFilterChange('voucherCountRange', {
                          ...filters.voucherCountRange,
                          min: e.target.value
                        })}
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        type="number"
                        placeholder="Max count"
                        value={filters.voucherCountRange.max}
                        onChange={(e) => handleFilterChange('voucherCountRange', {
                          ...filters.voucherCountRange,
                          max: e.target.value
                        })}
                      />
                    </Col>
                  </Row>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="processed">Processed</option>
                    <option value="returned">Returned</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Account Info for ST Review */}
      {isSTReview && accountInfo.length > 0 && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Header className="bg-light">
            <h6 className="mb-0">Account Information</h6>
          </Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap gap-2">
              {accountInfo.map((item, index) => (
                <Button
                  key={index}
                  variant="outline-primary"
                  size="sm"
                  onClick={() => {
                    setItem(item);
                    toggle1();
                  }}
                  className="d-flex align-items-center gap-2"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                  {item.account_name} ({item.bankName})
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          {error}
        </Alert>
      )}

      {/* Main Data Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Payment Batches</h6>
            <div className="d-flex align-items-center gap-3">
              <small className="text-muted">
                Showing {paginatedData.length} of {filteredData.length} batches
              </small>
              <Form.Select
                size="sm"
                style={{ width: 'auto' }}
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </Form.Select>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner animation="border" variant="primary" />
              <span className="ms-2">Loading payment schedules...</span>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-5">
              <FontAwesomeIcon icon={faInfoCircle} size="3x" className="text-muted mb-3" />
              <h5 className="text-muted">No payment batches found</h5>
              <p className="text-muted">
                {paymentSchedule.length === 0
                  ? "No batches available for processing"
                  : "Try adjusting your filters to see more results"
                }
              </p>
            </div>
          ) : (
            <CustomTable
              size="sm"
              fields={fields}
              data={paginatedData}
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={setCurrentPage}
              className="mb-0"
            />
          )}
        </Card.Body>

        {/* Pagination */}
        {totalPages > 1 && (
          <Card.Footer className="bg-white border-top">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Page {currentPage} of {totalPages}
              </small>
              <div className="d-flex gap-1">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  First
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  Last
                </Button>
              </div>
            </div>
          </Card.Footer>
        )}
      </Card>

      {/* Cash Backing Modal */}
      <Modal show={modalIsOpen} size="lg">
        <Modal.Header>
          <Modal.Title>Cash Backing Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <div
                onClick={() =>
                  setFocus((p) => (p === "cvc" ? "name" : "cvc"))
                }
              >
                <Cards
                  cvc={selected.cvv2 || "000"}
                  expiry={selected.expiry_date}
                  focused={focus}
                  name={selected.account_name?.slice(0, 30)}
                  number={selected.card}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mt-3">
                <h6>Account Details</h6>
                <p><strong>Account:</strong> {selected.account_name}</p>
                <p><strong>Bank:</strong> {selected.bankName}</p>
                <p><strong>Account Number:</strong> {selected.account_number}</p>
                <hr />
                <h5 className="text-success">
                  Balance: {selected.balance}
                </h5>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setModalIsOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* PIN Confirmation Modal */}
      <Modal show={modalIsOpen1}>
        <Modal.Header>
          <Modal.Title>PIN Confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <h6>Account: {item.account_name}</h6>
            <p className="text-muted">({item.account_number})</p>
          </div>
          <Form.Group>
            <Form.Label>Enter PIN</Form.Label>
            <Form.Control
              type="password"
              maxLength="4"
              placeholder="Enter 4-digit PIN"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="text-center"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={toggle1}>
            Cancel
          </Button>
          <CustomButton
            loading={balanceLoading}
            onClick={() => getBalance(item.id)}
            disabled={pin.length !== 4}
          >
            Confirm
          </CustomButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default STReceivingOffice;
